import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  Paper,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { UrlApi } from "../../../../services/apirest";
import DialogSelectProductos from "./componentes/DialogSelectProductos";
import ResumenFactura from "./componentes/ResumenFactura";
import LockIcon from "@mui/icons-material/Lock";
import axios from "axios";
import LinkPagoGenerado from "./componentes/LinkPagoGenerado";
import RenderRenglonLink from "./componentes/RenderRenglonLink";
import ActionBar from "./componentes/encabezado/ActionBar";
import TableHeader from "./componentes/encabezado/TableHeader";
import { putData } from "../../../../services/library";

const GenerarLinkPago = ({ open, onClose, dataPedido }) => {
  const [renglones, setRenglones] = useState([]);
  const [disBtn, setDisBtn] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("idle"); // idle, pending, success, error
  const [externalIntentionId, setExternalIntentionId] = useState(null);
  const [error, setError] = useState(null);
  const [mostrarLinkGenerado, setMostrarLinkGenerado] = useState(false);
  const [mostrarResumen, setMostrarResumen] = useState(false);
  const MERCADOPAGO_API_HIPERGAS = "https://mphipergas.netizar.com";
  const MERCADOPAGO_API_NATURAL = "https://mpnatural.netizar.com";

  // const MERCADOPAGO_API_HIPERGAS = "https://0ebe-190-2-10-4.ngrok-free.app";
  // const MERCADOPAGO_API_NATURAL = "https://0ebe-190-2-10-4.ngrok-free.app";

  // Estados para los totales
  const [subtotal, setSubtotal] = useState(0);
  const [totalIva, setTotalIva] = useState(0);
  const [totalGeneral, setTotalGeneral] = useState(0);

  // Usar dataPedido en lugar de pedido
  const pedido = dataPedido;

  const getRenglones = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${UrlApi}/envasado_pedidos_renglon/renglones/${pedido.uuid_pedido}`
      );
      if (!response.ok) {
        throw new Error("Error al obtener los renglones");
      }

      const data = await response.json();

      const renglonesProcesados = data.map((renglon) => {
        // Determinar si tiene precio fijo
        const tienePrecioEspecial = renglon.precio_fijo === null ? 0 : 1;

        // Si tiene precio fijo, usar ese precio para calculos
        const precioParaCalculos =
          tienePrecioEspecial === 1 ? renglon.precio_fijo : renglon.precio;

        const precioNum = parseFloat(precioParaCalculos);
        let precioEstado = null;

        // Solo verificar si hay un precio valido
        if (!isNaN(precioNum)) {
          if (
            renglon.precio_minimo !== null &&
            precioNum < renglon.precio_minimo
          ) {
            precioEstado = "min";
          } else if (
            renglon.precio_maximo !== null &&
            precioNum > renglon.precio_maximo
          ) {
            precioEstado = "max";
          } else {
            precioEstado = "ok";
          }
        }

        return {
          ...renglon,
          tiene_precio_especial: tienePrecioEspecial,
          precio: precioParaCalculos,
          precioEstado: precioEstado,
        };
      });

      setRenglones(renglonesProcesados);
      calcularTotales(renglonesProcesados);
    } catch (error) {
      console.error("Error obteniendo los renglones:", error);
    } finally {
      setLoading(false);
    }
  };

  // Función para calcular los totales
  const calcularTotales = (renglonesData) => {
    let subtotalAcumulado = 0;
    let ivaAcumulado = 0;
    let totalAcumulado = 0;

    renglonesData.forEach((renglon, index) => {
      const cantidad = parseFloat(renglon.cantidad) || 0;
      const precioConIva = parseFloat(renglon.precio) || 0;
      const porcentajeIva = parseFloat(renglon.iva) || 0;

      const totalRenglon = cantidad * precioConIva;
      const subtotalRenglon = totalRenglon / (1 + porcentajeIva / 100);
      const ivaRenglon = totalRenglon - subtotalRenglon;

      subtotalAcumulado += subtotalRenglon;
      ivaAcumulado += ivaRenglon;
      totalAcumulado += totalRenglon;
    });

    setSubtotal(subtotalAcumulado);
    setTotalIva(ivaAcumulado);
    setTotalGeneral(totalAcumulado);
  };

  useEffect(() => {
    if (open && pedido) {
      getRenglones();
    }
  }, [open, pedido]);

  const handleProductoChange = (index, value) => {
    const updatedRenglones = [...renglones];
    updatedRenglones[index].producto = value;
    setRenglones(updatedRenglones);
    calcularTotales(updatedRenglones);
  };

  const handleCantidadChange = (index, value) => {
    const updatedRenglones = [...renglones];
    updatedRenglones[index].cantidad = value;
    setRenglones(updatedRenglones);
    calcularTotales(updatedRenglones);
  };

  const handlePrecioChange = (index, value) => {
    const renglon = renglones[index];

    if (renglon.tiene_precio_especial === 1) {
      return; // No hacer nada si es precio fijo
    }

    const nuevoPrecio = Number(value);

    setRenglones((prev) => {
      const nuevos = [...prev];
      const currentRenglon = nuevos[index];
      currentRenglon.precio = value;

      if (value === "") {
        currentRenglon.precioEstado = null;
      } else if (isNaN(nuevoPrecio)) {
        currentRenglon.precioEstado = "invalido";
      } else if (
        currentRenglon.precio_minimo !== null &&
        nuevoPrecio < currentRenglon.precio_minimo
      ) {
        currentRenglon.precioEstado = "min";
      } else if (
        currentRenglon.precio_maximo !== null &&
        nuevoPrecio > currentRenglon.precio_maximo
      ) {
        currentRenglon.precioEstado = "max";
      } else {
        currentRenglon.precioEstado = "ok";
      }

      return nuevos;
    });

    const updatedRenglones = [...renglones];
    updatedRenglones[index].precio = value;
    calcularTotales(updatedRenglones);
  };

  const eliminarLinea = async (index) => {
    const renglonAEliminar = renglones[index];

    if (renglonAEliminar.id) {
      try {
        const url = `${UrlApi}/envasado_pedidos_renglon/${renglonAEliminar.id}`;
        const payload = { activo: 0 };

        const response = await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          alert(
            " Error\nNo se pudo eliminar el producto del servidor.\n\nPor favor, intente nuevamente."
          );
          return;
        }
      } catch (error) {
        console.error("Error eliminando el renglón:", error);
        alert(
          "⚠️ Error de Conexión\nNo se pudo conectar con el servidor para eliminar el producto.\n\nVerifique su conexión e intente nuevamente."
        );
        return;
      }
    }

    const updatedRenglones = renglones.filter((_, i) => i !== index);
    setRenglones(updatedRenglones);
    calcularTotales(updatedRenglones);
  };

  // const getProductos = async () => {
  //   try {
  //     const response = await fetch(`${UrlApi}/productos/envasados`);
  //     const data = await response.json();
  //     setProductos(data);
  //   } catch (error) {
  //     console.error("Error obteniendo productos:", error);
  //   }
  // };

  // useEffect(() => {
  //   getProductos();
  // }, []);

  const toggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };

  const handleProductSelect = (product) => {
    setSelectedProductIds((prev) => {
      const updated = new Set(prev);
      if (updated.has(product.idproducto)) {
        updated.delete(product.idproducto);
      } else {
        updated.add(product.idproducto);
      }
      return updated;
    });
  };

  const addSelectedProducts = async () => {
    const sucursal = localStorage.getItem("sucursal");

    try {
      const newRenglones = await Promise.all(
        Array.from(selectedProductIds).map(async (id) => {
          const product = productos.find((p) => p.idproducto === id);

          const resp = await fetch(
            `${UrlApi}/envasado_precios_especiales/xprodxclixsuc/${product.idproducto}/${pedido.id_cliente}/${sucursal}`
          );
          const precios = await resp.json();
          const precioData = precios[0] || {};

          // Determinar si hay precio fijo
          const tienePrecioEspecial = precioData.precio_fijo !== null ? 1 : 0;
          const precioFijo = precioData.precio_fijo || null;

          // Usar precio_fijo si existe, de lo contrario string vacío
          const precioInicial = tienePrecioEspecial === 1 ? precioFijo : "";

          return {
            producto: product?.idproducto || "",
            nombre: product?.nombre || "",
            abreviatura: product?.abreviatura || "",
            tiene_precio_especial: tienePrecioEspecial,
            precio: precioInicial,
            cantidad: 1,
            precio_minimo: precioData.precio_minimo ?? null,
            precio_maximo: precioData.precio_maximo ?? null,
            iva: product?.iva || 0,
            iva_cod_afip: product?.iva_cod_afip || 0,
            precio_fijo: precioFijo,
          };
        })
      );

      const updatedRenglones = [...renglones, ...newRenglones];
      setRenglones(updatedRenglones);
      calcularTotales(updatedRenglones);
      setSelectedProductIds(new Set());
      toggleModal();
    } catch (error) {
      console.error("Error al agregar renglones:", error);
      alert(
        "Error al obtener los precios especiales. Por favor, intente nuevamente."
      );
    }
  };

  const handleGuardar = async () => {
    setDisBtn(true);

    try {
      for (const renglon of renglones) {
        const isNuevo = !renglon.id;

        const payload = {
          uuid_pedido: pedido.uuid_pedido,
          producto: renglon.producto,
          cantidad: renglon.cantidad,
          precio: renglon.precio,
        };

        const url = isNuevo
          ? `${UrlApi}/envasado_pedidos_renglon/`
          : `${UrlApi}/envasado_pedidos_renglon/${renglon.id}`;

        const method = isNuevo ? "POST" : "PUT";

        const response = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error("Error guardando renglones");
      }

      setMostrarResumen(true);
    } catch (error) {
      console.error("Error al actualizar los precios:", error);
      alert("Error al guardar los precios. Por favor, intente nuevamente.");
      setDisBtn(false);
    }
  };

  const handleVolverAEditar = () => {
    setMostrarResumen(false);
    setDisBtn(false);
  };

  // Marca el pedido como con link de pago generado
const actualizarEstadoPedido = async (pedidoId) => {
  try {
    const response = await fetch(`${UrlApi}/envasado_pedidos/${pedidoId}`, {
      method: "PUT",
      headers: { 
        "Content-Type": "application/json" 
      },
      body: JSON.stringify({ 
        link_pago:1
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error HTTP: ${response.status}`
      );
    }

    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error(" Error actualizando estado del pedido:", error);
    throw error;
  }
};

const desactivarLinksAnteriores = async (pedidoId) => {
  const response = await fetch(`${UrlApi}/envasado_link_pago/xpedido/${pedidoId}`);
  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }
  const linksActivos = await response.json();

  for (const linkActivo of linksActivos) {
    const res = await fetch(`${UrlApi}/envasado_link_pago/${linkActivo.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ activo: 0 }),
    });
    if (!res.ok) {
      throw new Error(`Error desactivando link anterior: HTTP ${res.status}`);
    }
  }
};

const guardarLink = async (pedidoId, link) => {
  try {
    const response = await fetch(`${UrlApi}/envasado_link_pago/`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json" 
      },
      body: JSON.stringify({ 
        id_pedido: pedidoId,
        link: link.url,
        activo:1,
        usuario: localStorage.getItem("user"),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Error HTTP: ${response.status}`
      );
    }

    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error(" Error Guardando el link de pago:", error);
    throw error;
  }
};

const generarLinkPagoMercadoPago = async (pedido, montoFinal, renglones) => {
const urlEmpresa = pedido.empresa_cliente===1?MERCADOPAGO_API_HIPERGAS:MERCADOPAGO_API_NATURAL;

  try {
    const esEnvase = 0;

    const ordenData = {
      descripcion: `Pedido #${pedido.id} - ${renglones.length} productos`,
      monto: montoFinal,
      idPedido: pedido.id,
      esEnvase: esEnvase
    };

    const response = await axios.post(
      `${urlEmpresa}/api/link/create`,
      ordenData
    );

    if (!response.data.ok || !response.data.data) {
      throw new Error(
        response.data.message || "Error generando link"
      );
    }
// console.log(response);

    const backendExternalRef =
      response.data.data.external_reference;

    const backendPreferenceId =
      response.data.data.preference_id;

    return {
      linkData: {
        external_id: backendExternalRef,
        url: response.data.data.init_point,
        id: backendPreferenceId,
      },
      externalIntentionId: backendExternalRef
    };

  } catch (error) {
    console.error("❌ Error generando link:", error);
    console.error(
      "Detalle:",
      error.response?.data || error.message
    );
    throw error;
  }
};

  // Función principal que actualiza estado y genera link
const procesarGeneracionLinkCompleta = async (montoFinal) => {
  if (!pedido || !pedido.id) {
    throw new Error("No hay un pedido válido");
  }

  if (!(montoFinal > 0)) {
    throw new Error("El total debe ser mayor a 0");
  }

  // 1. Generar link de pago con Mercado Pago
  const { linkData, externalIntentionId } = await generarLinkPagoMercadoPago(
    pedido,
    montoFinal,
    renglones
  );

  // 2. Guardar el link (desactivando los anteriores del pedido)
  await desactivarLinksAnteriores(pedido.id);
  await guardarLink(pedido.id, linkData);

  // 3. Marcar el pedido con link de pago
  await actualizarEstadoPedido(pedido.id);

  return {
    success: true,
    linkData,
    externalIntentionId,
    message: "Link generado exitosamente"
  };
};

const handleGenerarLinkPago = async (montoFinal) => {
  setPaymentStatus("pending");
  setError(null);

  try {
    setLoading(true);

    const resultado = await procesarGeneracionLinkCompleta(Number(montoFinal));

    setPaymentLink(resultado.linkData);
    setExternalIntentionId(resultado.externalIntentionId);
    setPaymentStatus("success");

    setMostrarResumen(false);
    setMostrarLinkGenerado(true);
    setLoading(false);

  } catch (err) {
    console.error("❌ Error generando link:", err);
    setError(
      err.response?.data?.message ||
        err.message ||
        "Error generando link de pago"
    );
    setPaymentStatus("error");
    setLoading(false);
  }
};

  const copiarLink = () => {
    if (!paymentLink?.url) return;

    navigator.clipboard
      .writeText(paymentLink.url)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Error copiando:", err);
        setError("No se pudo copiar al portapapeles");
      });
  };

  useEffect(() => {
    if (renglones.length > 0) {
      const allPricesValid = renglones.every((renglon) => {
        // Si tiene precio fijo, siempre es válido
        if (renglon.tiene_precio_especial === 1) {
          return true;
        }

        return (
          renglon.precio !== null &&
          renglon.precio !== "" &&
          !isNaN(Number(renglon.precio)) &&
          (renglon.precioEstado === "ok" || renglon.precioEstado === null)
        );
      });

      setDisBtn(!allPricesValid);
    } else {
      setDisBtn(true);
    }
  }, [renglones]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <Box justifyContent="center" backgroundColor="#1F618D">
          <DialogTitle>
            <Typography variant="h6" align="center" color="#ffffff">
              Generar link de pago
            </Typography>
          </DialogTitle>
        </Box>

        <DialogContent>
          {error && !loading && (
            <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
              {error}
            </Alert>
          )}
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress />
            </Box>
          ) : mostrarLinkGenerado ? (
            <LinkPagoGenerado
              paymentLink={paymentLink}
              pedido={pedido}
              totalGeneral={totalGeneral}
              linkCopied={linkCopied}
              onCopiarLink={copiarLink}
              onVolver={() => {
                setMostrarLinkGenerado(false);
                setMostrarResumen(true);
              }}
              renglones={renglones}
            />
          ) : mostrarResumen ? (
           <ResumenFactura
             pedido={pedido}
             renglones={renglones}
             subtotal={subtotal}
             total={totalIva}
             totalGeneral={totalGeneral}
             onVolverEditar={handleVolverAEditar}
             onConfirmarFinal={handleGenerarLinkPago}
           />
          ) : (
            <>
              

              <Divider sx={{ mb: 2 }} />

              <TableHeader />

              {renglones.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary">
                    No hay productos agregados. Haz clic en "Agregar Productos"
                    para comenzar.
                  </Typography>
                </Box>
              ) : (
                renglones.map((renglon, index) => (
                  <RenderRenglonLink
                    key={index}
                    renglon={renglon}
                    index={index}
                    handleCantidadChange={handleCantidadChange}
                    handleProductoChange={handleProductoChange}
                    handlePrecioChange={handlePrecioChange}
                    eliminarLinea={eliminarLinea}
                  />
                ))
              )}
              <ActionBar
                onAgregarProductos={() => setIsModalVisible(true)}
                subtotal={subtotal}
                totalIva={totalIva}
                totalGeneral={totalGeneral}
              />
              <Grid
                mt={1}
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Button variant="outlined" onClick={onClose} color="error">
                  Volver
                </Button>
                <Button
                  disabled={disBtn}
                  variant="contained"
                  onClick={handleGuardar}
                  color="success"
                >
                  Confirmar
                </Button>
              </Grid>
            </>
          )}
        </DialogContent>
      </Dialog>

      {isModalVisible && (
        <DialogSelectProductos
          isModalVisible={isModalVisible}
          toggleModal={toggleModal}
          handleProductSelect={handleProductSelect}
          selectedProductIds={selectedProductIds}
          addSelectedProducts={addSelectedProducts}
        />
      )}
    </>
  );
};

export default GenerarLinkPago;
